import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { api, getSession, onSessionChange, setSession } from '../api/client'
import type { Me, Tokens } from '../api/types'

interface AuthState {
  me: Me | null
  loading: boolean
  tenant: string | null
  login: (tenant: string, email: string, password: string, mfaCode?: string) => Promise<void>
  logout: () => Promise<void>
  can: (permission: string) => boolean
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)
  const [tenant, setTenant] = useState<string | null>(getSession()?.tenant ?? null)

  const loadMe = useCallback(async () => {
    if (!getSession()) {
      setMe(null)
      setLoading(false)
      return
    }
    try {
      setMe(await api.get<Me>('/auth/me'))
    } catch {
      // Сессия протухла окончательно (refresh тоже не прошёл) — выходим.
      setSession(null)
      setMe(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadMe()
    return onSessionChange((next) => {
      setTenant(next?.tenant ?? null)
      if (!next) setMe(null)
    })
  }, [loadMe])

  const login = useCallback(
    async (tenantSlug: string, email: string, password: string, mfaCode?: string) => {
      const tokens = await api.post<Tokens>('/auth/login', {
        tenant: tenantSlug,
        email,
        password,
        mfa_code: mfaCode || null,
      })
      setSession({
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        tenant: tenantSlug,
      })
      setLoading(true)
      await loadMe()
    },
    [loadMe],
  )

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } catch {
      /* даже если сервер недоступен, локальную сессию гасим */
    }
    setSession(null)
    setMe(null)
  }, [])

  const can = useCallback(
    (permission: string) =>
      !!me && (me.permissions.includes(permission) || me.permissions.includes('platform.admin')),
    [me],
  )

  const value = useMemo(
    () => ({ me, loading, tenant, login, logout, can }),
    [me, loading, tenant, login, logout, can],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth вне AuthProvider')
  return context
}
